import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { getCropNameFromId } from 'utils/getCropNameFromId'
import type { TUnitConverterTypicalReturn } from '../types'

const DEFAULT_DECIMAL_PLACES = 1

type TCropDegreeDaysUnit = 'c' | 'f'

const cropDegreeDaysFunctionMaker =
  ({ cumulative }: { cumulative: boolean }) =>
  (
    degreeDaysAccumulatedTemperature: number | null = null,
    {
      cropId = -1,
      decimalPlaces = DEFAULT_DECIMAL_PLACES,
      inputUnit = 'c',
      outputUnit = userDetailsStore.getState().temperature === 'IMPERIAL' ? 'f' : 'c',
    }: {
      cropId?: number
      decimalPlaces?: number
      inputUnit?: TCropDegreeDaysUnit
      outputUnit?: TCropDegreeDaysUnit
    } = {},
  ) => {
    let convertedValue: number | null

    const shouldConvertFromCToF = inputUnit === 'c' && outputUnit === 'f'
    const shouldConvertFromFToC = inputUnit === 'f' && outputUnit === 'c'

    if (isNil(degreeDaysAccumulatedTemperature)) {
      convertedValue = null
    } else if (shouldConvertFromCToF) {
      convertedValue = degreeDaysAccumulatedTemperature * 1.8
    } else if (shouldConvertFromFToC) {
      convertedValue = degreeDaysAccumulatedTemperature / 1.8
    } else {
      // assume there is no conversion required
      convertedValue = Number(degreeDaysAccumulatedTemperature)
    }

    const unitSymbol =
      outputUnit === 'c'
        ? translate.measurements.degreeDaysCelsius.unit()
        : translate.measurements.degreeDaysFahrenheit.unit()

    return {
      categoryTitle: () =>
        translate.phrases.templates('{{label}} ({{unitSymbol}})', {
          label: translate.phrases.banyanApp('Degree Days'),
          unitSymbol,
        }),
      categoryTitleWithoutUnit: () => translate.phrases.banyanApp('Degree Days'),
      defaultNumberOfDecimalPlaces: () => DEFAULT_DECIMAL_PLACES,
      suffix: () => unitSymbol,
      shortTitle: () => {
        if (cumulative) {
          return translate.phrases.banyanApp('{{cropName}} Cumulative Degree Days', {
            cropName: getCropNameFromId(cropId),
          })
        }

        return translate.phrases.banyanApp('{{cropName}} Degree Days', {
          cropName: getCropNameFromId(cropId),
        })
      },
      title: () => {
        if (cumulative) {
          return translate.phrases.templates('{{label}} ({{unitSymbol}})', {
            label: translate.phrases.banyanApp('{{cropName}} Cumulative Degree Days', {
              cropName: getCropNameFromId(cropId),
            }),
            unitSymbol,
          })
        }

        return translate.phrases.templates('{{label}} ({{unitSymbol}})', {
          label: translate.phrases.banyanApp('{{cropName}} Degree Days', {
            cropName: getCropNameFromId(cropId),
          }),
          unitSymbol,
        })
      },
      titleWithoutUnit: () => {
        if (cumulative) {
          return translate.phrases.banyanApp('{{cropName}} Cumulative Degree Days', {
            cropName: getCropNameFromId(cropId),
          })
        }

        return translate.phrases.banyanApp('{{cropName}} Degree Days', {
          cropName: getCropNameFromId(cropId),
        })
      },
      value: () => {
        if (isNil(convertedValue)) return null

        return +convertedValue?.toFixed(decimalPlaces)
      },
      valueAsString: () => {
        return outputUnit === 'c'
          ? translate.measurements.degreeDaysCelsius.value(convertedValue, decimalPlaces)
          : translate.measurements.degreeDaysFahrenheit.value(convertedValue, decimalPlaces)
      },
      valueWithSuffix: () => {
        return outputUnit === 'c'
          ? translate.measurements.degreeDaysCelsius.valueWithUnit(convertedValue, decimalPlaces)
          : translate.measurements.degreeDaysFahrenheit.valueWithUnit(convertedValue, decimalPlaces)
      },
      valueWithNoRounding: () => convertedValue,
    }
  }

type TCropDegreeDaysOptions = 'cropDegreeDays' | 'cropDegreeDaysCumulative'

export const cropDegreeDays: Record<
  TCropDegreeDaysOptions,
  (
    degreeDaysAccumulatedTemperature?: number | null,
    options?: {
      cropId?: number
      decimalPlaces?: number
      outputUnit?: TCropDegreeDaysUnit
      inputUnit?: TCropDegreeDaysUnit
    },
  ) => TUnitConverterTypicalReturn
> = {
  cropDegreeDays: cropDegreeDaysFunctionMaker({ cumulative: false }),
  cropDegreeDaysCumulative: cropDegreeDaysFunctionMaker({ cumulative: true }),
}
